import { NavigateFunction } from "react-router-dom";
import {
  InterviewSession,
  MatchResult,
  Userprofile,
} from "../../Interface/Index";
import ProfileMatcherService from "../../Services/profile.matcher.service";
import AgentService from "../../Services/agent/agent.service";
import messages from "../../Services/agent/messages";
import InterviewService from "../../Services/interview.service";
import {
  JobApplication,
  JobApplicationStatus,
} from "../../Entities/job-application.entity";
import JobApplicationService from "../../Services/job.application.service";

export interface RequirementsHandlersConfig {
  navigate: NavigateFunction;
  profileMatcherService: ProfileMatcherService;
  agentService: AgentService;
  interviewService: InterviewService;
  jobApplicationService: JobApplicationService;
  appliedJob: JobApplication | null;
  jobDescription: any;
  enabledInterviewRoundTabs: any[];
  setAppliedJob: (job: JobApplication) => void;
  setShowResultTab: (show: boolean) => void;
  setShowOnlineInterviewTab: (show: boolean) => void;
  setLoading: (loading: boolean) => void;
  setLoadingMessage: (message: string | null) => void;
  enableRounds: (roundId: string, sessionId: string) => void;
  addSession: (session: InterviewSession) => void;
}

export class RequirementsHandlers {
  private config: RequirementsHandlersConfig;
  private PROFILE_MATCHED_JOB_POST_ID_KEY = "profileMatchedJobPostId";
  private IS_PROFILE_MATCHING_KEY = "isProfileMatching";

  constructor(config: RequirementsHandlersConfig) {
    this.config = config;
  }

  private setProfileMatchStartedOnLocalStorage(jobPostId: string) {
    localStorage.setItem(this.PROFILE_MATCHED_JOB_POST_ID_KEY, jobPostId);
    localStorage.setItem(this.IS_PROFILE_MATCHING_KEY, "true");
  }

  private clearProfileMatchTrackOnLocalStorage() {
    localStorage.removeItem(this.PROFILE_MATCHED_JOB_POST_ID_KEY);
    localStorage.removeItem(this.IS_PROFILE_MATCHING_KEY);
  }

  private getProfileMatchTrackFromLocalStorage() {
    const jobPostId = localStorage.getItem(this.PROFILE_MATCHED_JOB_POST_ID_KEY);
    const isProfileMatching = localStorage.getItem(this.IS_PROFILE_MATCHING_KEY);
    return {
      jobPostId,
      isProfileMatching,
    };
  }

  handleUnqualifiedProfileMatchResult = async (jobApplicationId: string) => {
    const statusToPoll = "rejected";
    const updatedApplication = await this.checkApplicationStatus(
      jobApplicationId,
      statusToPoll
    );
    if (updatedApplication) {
      this.config.setAppliedJob(updatedApplication);
    }
    setTimeout(() => {
      this.config.setLoading(false);
      this.config.setLoadingMessage(null);
    }, 500);
    this.config.setShowResultTab(true);
    this.config.agentService.setCurrentMessage(messages.MATCH_RESULT_UNQUALIFIED, "", "");
    this.clearProfileMatchTrackOnLocalStorage();
    this.config.navigate(`/${this.config.jobDescription?._id}/result`);
    return;
  };

  handleQualifiedProfileMatchResult = async (jobApplicationId: string) => {
    const isInterviewRoundsExists = this.config.enabledInterviewRoundTabs.length > 0;
    if (!isInterviewRoundsExists) {
      await this.handleNoInterviewExists(jobApplicationId);
      this.clearProfileMatchTrackOnLocalStorage();
      return;
    } else {
      await this.handleNextInterviewRound(jobApplicationId);
      this.clearProfileMatchTrackOnLocalStorage();
      return;
    }
  };

  checkApplicationStatus = async (
    id: string,
    status: JobApplicationStatus
  ): Promise<JobApplication> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const jobApplication =
            await this.config.jobApplicationService.findJobApplicationById(id);
          if (jobApplication && jobApplication.status === status) {
            clearInterval(interval);
            resolve(jobApplication);
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  checkProfileMatchCompletion = async (
    id: string
  ): Promise<JobApplication> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const jobApplication =
            await this.config.jobApplicationService.findJobApplicationById(id);
          if (jobApplication && jobApplication.profileMatchStatus) {
            clearInterval(interval);
            resolve(jobApplication);
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  handleNoInterviewExists = async (jobApplicationId: string) => {
    this.config.setLoadingMessage("Setting up online interview...");
    this.config.agentService.setCurrentMessage(messages.EVALUATION_QUALIFIED, "", "");
    try {
      const statusToCheck: JobApplicationStatus =
        this.config.jobDescription?.onlineInterview ? "interview" : "shortlisted";

      const updatedJobApplication = await this.checkApplicationStatus(
        jobApplicationId,
        statusToCheck
      );

      if (updatedJobApplication) {
        this.config.setAppliedJob(updatedJobApplication);

        if (statusToCheck === "shortlisted") {
          this.config.agentService.setCurrentMessage(
            messages.WAITING_FOR_INTERVIEW_SCHEDULE,
            "",
            "Select Slot"
          );
          this.config.setShowResultTab(true);
          this.config.navigate(`/${this.config.jobDescription?._id}/result`);
        } else if (statusToCheck === "interview") {
          this.config.setShowOnlineInterviewTab(true);
          this.config.agentService.setCurrentMessage(
            messages.WAITING_FOR_INTERVIEW_SCHEDULE,
            "",
            "Select Slot"
          );
          this.config.navigate(`/${this.config.jobDescription?._id}/online-interview`);
        }
      }
    } catch (error) {
      console.log("handleNoInterviewExists error", error);
    } finally {
      this.config.setLoading(false);
      this.config.setLoadingMessage(null);
    }
  };

  handleNextInterviewRound = async (jobApplicationId: string) => {
    this.config.agentService.setCurrentMessage(
      messages.WAITING_FOR_INTERVIEW_SESSION_CREATION,
      "",
      ""
    );
    this.config.setLoadingMessage("Creating interview session...");

    const pollForSession = async (): Promise<InterviewSession[]> => {
      try {
        const sessions = await this.config.interviewService.getAllInterviewSessionById(
          jobApplicationId
        );

        if (
          sessions &&
          sessions.length > 0 &&
          sessions[0].isQuestionsGenerated
        ) {
          return sessions;
        } else {
          console.log("No session found. Retrying...");
          await new Promise((resolve) => setTimeout(resolve, 2000));
          return await pollForSession();
        }
      } catch (error) {
        console.error("Error fetching session:", error);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        return await pollForSession();
      }
    };

    const sessions = await pollForSession();

    if (sessions && sessions.length > 0) {
      this.config.agentService.setCurrentMessage(messages.GENERATING_QUESTIONNAIRE, "", "");
      this.config.setLoadingMessage("Generating questionnaire...");
      const firstSession = sessions[0];
      if (firstSession && firstSession.isQuestionsGenerated) {
        const roundName = this.config.enabledInterviewRoundTabs.find(
          (round) => round.roundId === firstSession.interviewRoundId
        )?.roundName;
        this.config.enableRounds(firstSession.interviewRoundId, firstSession._id);
        this.config.addSession(firstSession);
        this.config.setLoading(false);
        this.config.setLoadingMessage(null);
        this.config.agentService.setCurrentMessage(
          messages.WAITING_FOR_INTERVIEW_ROUND_COMPLETION,
          { roundName: roundName },
          "View Questions"
        );
        this.config.navigate(
          `/${this.config.jobDescription?._id}/interview-round/${firstSession._id}`
        );
      }
    }
  };

  handleFileUploadAndAnalyze = async (file: File) => {
    if (!this.config.appliedJob) {
      console.error("handleFileUploadAndAnalyze -> no applied job");
      return;
    }
    if (!this.config.jobDescription || !this.config.jobDescription._id) {
      console.error("handleFileUploadAndAnalyze -> no job description or id");
      return;
    }
    this.config.setLoading(true);
    this.config.setLoadingMessage("Matching your resume...");
    this.config.agentService.setCurrentMessage(messages.WAITING_FOR_MATCH_RESULTS, "", "");
    const matchResultPromise = this.config.profileMatcherService.uploadResumeToS3AndMatchProfile(
      file,
      this.config.appliedJob._id
    );
    this.setProfileMatchStartedOnLocalStorage(this.config.jobDescription._id);
    const matchResult = await matchResultPromise;

    if (!matchResult) return;

    if (matchResult.status === "unqualified") {
      return await this.handleUnqualifiedProfileMatchResult(
        matchResult?.jobApplicationId
      );
    } else if (matchResult.status === "qualified") {
      return await this.handleQualifiedProfileMatchResult(
        matchResult?.jobApplicationId
      );
    }
  };

  handleContinueWithProfile = async (profile: Userprofile) => {
    if (!this.config.appliedJob) {
      console.error("handleContinueWithProfile -> no applied job");
      return;
    }
    if (!this.config.jobDescription || !this.config.jobDescription._id) {
      console.error("handleContinueWithProfile -> no job description or id");
      return;
    }

    this.config.setLoading(true);
    this.config.setLoadingMessage("Matching your resume...");
    this.config.agentService.setCurrentMessage(messages.WAITING_FOR_MATCH_RESULTS, "", "");
    const matchResultPromise = this.config.profileMatcherService.matchResumeWithJDWithoutUploadingFile(
      this.config.appliedJob?._id,
      profile._id
    );
    this.setProfileMatchStartedOnLocalStorage(this.config.jobDescription._id);
    const matchResult = await matchResultPromise;

    if (!matchResult) return;

    if (matchResult.status === "unqualified") {
      return await this.handleUnqualifiedProfileMatchResult(
        matchResult?.jobApplicationId
      );
    } else if (matchResult.status === "qualified") {
      return await this.handleQualifiedProfileMatchResult(
        matchResult?.jobApplicationId
      );
    }
  };

  handleProfileMatchRecovery = async () => {
    if (!this.config.appliedJob) {
      console.log("No application found");
      return;
    }

    const { isProfileMatching, jobPostId } =
      this.getProfileMatchTrackFromLocalStorage();

    if (
      isProfileMatching === "true" &&
      this.config.jobDescription &&
      this.config.jobDescription._id === jobPostId
    ) {
      this.config.setLoading(true);
      this.config.setLoadingMessage("Waiting for match results...");
      setTimeout(() => {
        this.config.agentService.setCurrentMessage(
          messages.WAITING_FOR_MATCH_RESULTS,
          "",
          ""
        );
      }, 100);
      const matchResult = this.config.appliedJob?.profileMatchStatus;
      if (matchResult) {
        if (matchResult.status === "qualified") {
          return await this.handleQualifiedProfileMatchResult(this.config.appliedJob?._id);
        } else {
          return await this.handleUnqualifiedProfileMatchResult(this.config.appliedJob?._id);
        }
      } else {
        const updatedJobApplication = await this.checkProfileMatchCompletion(
          this.config.appliedJob?._id
        );
        if (updatedJobApplication?.profileMatchStatus?.status === "qualified") {
          return await this.handleQualifiedProfileMatchResult(this.config.appliedJob?._id);
        } else {
          return await this.handleUnqualifiedProfileMatchResult(this.config.appliedJob?._id);
        }
      }
    } else {
      return;
    }
  };
}
